import { CheckCircle2 } from 'lucide-react';
import { Card, Eyebrow, IconTile } from '@/shared/components';
import { formatBytes, formatRelative } from '@/shared/lib/format';

/** 5 GB Soft-Plan-Kontingent (Local-FS hat kein hartes Limit; reine Orientierung). */
const STORAGE_QUOTA_BYTES = 5 * 1024 * 1024 * 1024;

interface Props {
  totalFiles: number;
  totalSizeBytes: number;
  occupiedCategories: number;
  /** Neueste automatisch abgelegte Ausgangs-Rechnung (fileName + ISO-uploadedAt) oder null. */
  lastAutoFiled: { fileName: string; uploadedAt: string } | null;
}

export function AkteAutoFilingBanner({
  totalFiles,
  totalSizeBytes,
  occupiedCategories,
  lastAutoFiled,
}: Props) {
  const pct = Math.min(100, Math.round((totalSizeBytes / STORAGE_QUOTA_BYTES) * 100));

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-[1.7fr_1fr]">
      <Card variant="dark" className="flex items-start gap-4 p-6">
        <IconTile variant="accent" size="lg" className="shrink-0">
          <CheckCircle2 size={26} strokeWidth={2} />
        </IconTile>
        <div className="min-w-0">
          <h2 className="text-paper font-serif text-xl font-medium tracking-tight">
            Automatisches Ablegen
          </h2>
          <p className="text-paper/70 mt-1 text-sm">
            Ausgangs-Rechnungen aus Buchungen landen automatisch in{' '}
            <span className="text-paper font-medium">03 Rechnungen</span> — ohne manuellen Upload.
          </p>
          <div className="text-paper/60 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="bg-sage h-1.5 w-1.5 rounded-full" />
              <span className="text-paper/80">Aktiv</span>
            </span>
            {lastAutoFiled ? (
              <span className="tabular-nums">
                Zuletzt: <span className="text-paper/90 font-medium">{lastAutoFiled.fileName}</span>{' '}
                {formatRelative(lastAutoFiled.uploadedAt)}
              </span>
            ) : (
              <span>Noch keine automatisch abgelegte Rechnung.</span>
            )}
          </div>
        </div>
      </Card>

      <Card className="flex flex-col justify-center p-6">
        <Eyebrow variant="muted">Speicher</Eyebrow>
        <div className="text-ink mt-2 font-serif text-3xl font-normal tabular-nums">
          {formatBytes(totalSizeBytes)}
          <span className="text-ink-faint ml-1 text-base">/ 5 GB</span>
        </div>
        <div className="bg-card-alt mt-3 h-2 overflow-hidden rounded-full">
          <div
            className="bg-sage h-full rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-ink-soft mt-3 flex items-center justify-between text-xs">
          <span className="tabular-nums">
            {totalFiles} {totalFiles === 1 ? 'Datei' : 'Dateien'}
          </span>
          <span className="tabular-nums">
            {occupiedCategories} {occupiedCategories === 1 ? 'Kategorie' : 'Kategorien'} belegt
          </span>
        </div>
      </Card>
    </div>
  );
}
